import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';

type ServicesFabricationMechanicalProps = {
  onBack: () => void;
};

export default function ServicesFabricationMechanical({ onBack }: ServicesFabricationMechanicalProps): React.ReactElement {
  return (
    <section className="bg-slate-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
              Service Three
            </span>
            <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
              Fabrication, Refractory & Mechanical
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
              Our mechanical division provides the physical backbone for industrial operations, combining workshop
              precision with on-site agility.
            </p>
          </div>

          {/* Core Mechanical Offerings */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              Core Mechanical Offerings
            </h3>
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Structural Fabrication */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <BuildOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Structural Fabrication</h4>
                </div>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Specialized Pipe Fabrication and Installation for high-pressure environments.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Steel Erection for industrial workshops, warehouses, and plant extensions.</span>
                  </li>
                </ul>
              </div>

              {/* Rotating Equipment Excellence */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <SettingsOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Rotating Equipment Excellence</h4>
                </div>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>
                      <strong className="font-semibold text-slate-900">Pumps & Motors:</strong> Selection, installation,
                      and complete overhaul services.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>
                      <strong className="font-semibold text-slate-900">Custom Parts:</strong> Manufacture of hard-to-find
                      spare parts to keep aging machinery operational.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Maintenance & Reliability */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <PrecisionManufacturingOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Maintenance & Reliability</h4>
                </div>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>
                      <strong className="font-semibold text-slate-900">Precision Alignment:</strong> Laser alignment
                      services to prevent premature bearing failure and reduce vibration.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>
                      <strong className="font-semibold text-slate-900">Condition Monitoring:</strong> Predictive
                      maintenance schedules based on real-time equipment data.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Technical Manpower Outsourcing */}
          <div className="mb-10">
            <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200 max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <PeopleOutlinedIcon fontSize="medium" />
                </div>
                <h4 className="text-lg font-semibold text-slate-900">Technical Manpower Outsourcing</h4>
              </div>
              <p className="text-sm leading-relaxed text-slate-600">
                We provide skilled engineering personnel for plant shutdowns, maintenance cycles, and specialized
                project placements.
              </p>
            </div>
          </div>

          {/* Our Commitment */}
          <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <EngineeringOutlinedIcon className="text-white" fontSize="large" />
              <h4 className="text-2xl font-semibold text-white">Our Commitment</h4>
            </div>
            <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
              By utilizing advanced alignment and condition monitoring, we help our clients extend the Mean Time
              Between Failures (MTBF) for their most critical assets.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


